/**
 * Fee basis
 */
export enum FeeBasis {
  PerUse = 'Per Use',
  Hourly = 'Hourly',
  _12Hour = '12 Hour',
  _24Hour = '24 Hour',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Annual = 'Annual',
  NoFee = 'No Fee',
  FeeNotDisclosed = 'Fee Not Disclosed'
}


/**
 * Business service/fee model
 */
export class BusinessService {
  /**
   * Unique identifier for this record in the database
   */
  serviceId: number;

  /**
   * Service/fee description (e.g., "Landing")
   */
  description: string;

  /**
   * Fee amount (USD)
   */
  fee: number;

  /**
   * Fee basis (Annual, Monthly, Per Use, etc.)
   */
  feeBasis: FeeBasis;

  /**
   * Free-form comment describing the service/fee (e.g., if it applies only to a specific aircraft type)
   */
  serviceNote: string;

  /**
   * Date record was last updated
   */
  updateDate: Date;

  /**
   * User ID who last updated the record
   */
  updateBy: Date;

  /**
   * Aircraft type this fee applies to
   */
  aircraftType: string;

  /**
   * Boolean to specify if there is a waiver to the fee
   */
  waiver: boolean;

  /**
   * The gallons of fuel required to get the waiver
   */
  waiverFuelQty: number;

  /**
   * Parses business service
   * @param data {any} Business service as returned by API
   * @return {BusinessService} Parsed business service
   */
  static parse(data: any): BusinessService {
    return {
      serviceId: data.serviceId,
      description: data.description,
      fee: data.fee,
      feeBasis: BusinessService.parseFeeBasis(data.feeBasis),
      serviceNote: data.serviceNote,
      updateDate: data.updateDate ? new Date(data.updateDate) : null,
      updateBy: data.updateBy,
      aircraftType: data.aircraftType,
      waiver: data.waiver,
      waiverFuelQty: data.waiverFuelQty
    };
  }

  /**
   * Parses fee basis
   * @param data {any} Fee basis as returned by API
   * @return {FeeBasis} Parsed fee basis
   */
  static parseFeeBasis(data: any): FeeBasis {
    switch (data) {
      case '12 Hour':
        return FeeBasis._12Hour;
      case 'Annual':
        return FeeBasis.Annual;
      case 'Monthly':
        return FeeBasis.Monthly;
      case 'No Fee':
        return FeeBasis.NoFee;
      case 'Hourly':
        return FeeBasis.Hourly;
      case 'Weekly':
        return FeeBasis.Weekly;
      case 'Per Use':
        return FeeBasis.PerUse;
      case '24 Hour':
        return FeeBasis._24Hour;
      case 'Fee Not Disclosed':
        return FeeBasis.FeeNotDisclosed;
      default:
        return null;
    }
  }

  /**
   * Formats fee basis for display
   * @param value {FeeBasis} Fee basis
   * @return {string} Formatted fee basis
   */
  static formatFeeBasis(value: FeeBasis): string {
    switch (value) {
      case FeeBasis._12Hour:
        return '12 Hour';
      case FeeBasis.Annual:
        return 'Annual';
      case FeeBasis.Monthly:
        return 'Monthly';
      case FeeBasis.NoFee:
        return 'No Fee';
      case FeeBasis.Hourly:
        return 'Hourly';
      case FeeBasis.Weekly:
        return 'Weekly';
      case FeeBasis.PerUse:
        return 'Per Use';
      case FeeBasis._24Hour:
        return '24 Hour';
      case FeeBasis.FeeNotDisclosed:
        return 'Fee Not Disclosed';
      default:
        return null;
    }
  }
}
